import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { callEdgeFunction } from '../lib/edgeFunctions';

export interface ChatMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface ChatConversation {
  id: string;
  user_id: string;
  title: string | null;
  context: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export function useChatConversations() {
  return useQuery({
    queryKey: ['ai-conversations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_conversations')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return data as ChatConversation[];
    },
    staleTime: 30_000,
  });
}

export function useChatMessages(conversationId: string | null) {
  return useQuery({
    queryKey: ['ai-messages', conversationId],
    queryFn: async () => {
      if (!conversationId) return [];
      const { data, error } = await supabase
        .from('ai_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as ChatMessage[];
    },
    enabled: !!conversationId,
    staleTime: 10_000,
  });
}

interface SendMessageParams {
  conversationId: string | null;
  message: string;
  context: Record<string, unknown>;
  images?: Array<{ base64: string; mediaType: string }>;
}

interface SendMessageResult {
  conversation_id: string;
  message: {
    id: string;
    role: 'assistant';
    content: string;
    metadata: Record<string, unknown>;
  };
  title?: string;
}

export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: SendMessageParams): Promise<SendMessageResult> => {
      const body: Record<string, unknown> = {
        message: params.message,
        context: params.context,
      };
      if (params.conversationId) {
        body.conversation_id = params.conversationId;
      }
      if (params.images && params.images.length > 0) {
        body.images = params.images;
      }

      const result = await callEdgeFunction('ai-chat', body);
      return result as unknown as SendMessageResult;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ai-messages', data.conversation_id] });
      queryClient.invalidateQueries({ queryKey: ['ai-conversations'] });
    },
  });
}

export function useDeleteConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId: string) => {
      const { error } = await supabase
        .from('ai_conversations')
        .delete()
        .eq('id', conversationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-conversations'] });
    },
  });
}
